import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Microphone, MicrophoneSlash, Phone, PhoneX } from 'phosphor-react';
import { doc, updateDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';

// ============================================================================
// PLANNING AUDIO CHANNEL
// ============================================================================
// Flow:
//   Host clicks mic button
//     → "Wil je [naam] spreken?" bevestigingskaart
//     → Host bevestigt → Firestore callSignal 'ring' + Jitsi opent
//     → User 2 krijgt onSnapshot update → inkomende ring kaart (neem op / wijs af)
//     → User 2 accepteert → Jitsi opent bij User 2
// ============================================================================

// ── BLE Bell Sound (Web Audio API) ─────────────────────────────────────────
function createBLERing(ctx: AudioContext): () => void {
  let stopped = false;
  let timeoutId: ReturnType<typeof setTimeout>;

  const playTone = (freq: number, start: number, dur: number) => {
    if (stopped) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, start);
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(0.3, start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
    osc.start(start);
    osc.stop(start + dur);
  };

  const ring = () => {
    if (stopped) return;
    const now = ctx.currentTime;
    playTone(880,  now,        0.18);
    playTone(1046, now + 0.22, 0.18);
    playTone(1318, now + 0.44, 0.24);
    timeoutId = setTimeout(ring, 2600);
  };
  ring();

  return () => {
    stopped = true;
    clearTimeout(timeoutId);
  };
}

interface PlanningAudioChannelProps {
  connectionId: string;
  currentUserId: string;
  currentUserName: string;
  otherUserName?: string;
}

type CallSignalAction = 'ring' | 'hangup' | 'declined' | 'accepted' | 'idle';

const PlanningAudioChannel: React.FC<PlanningAudioChannelProps> = ({
  connectionId,
  currentUserId,
  currentUserName,
  otherUserName = 'Connectie',
}) => {
  // Caller state
  const [showConfirm, setShowConfirm]       = useState(false);  // "Wil je spreken?" kaart
  const [isOpen, setIsOpen]                 = useState(false);
  const [isConnecting, setIsConnecting]     = useState(false);

  // Callee state
  const [incomingRing, setIncomingRing]     = useState(false);
  const [incomingFrom, setIncomingFrom]     = useState('');
  const [incomingCallerId, setIncomingCallerId] = useState('');

  // Mini-toast state
  const [toast, setToast]                   = useState<{ msg: string; type: 'info' | 'success' | 'error' } | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showMiniToast = useCallback((msg: string, type: 'info' | 'success' | 'error' = 'info') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ msg, type });
    toastTimerRef.current = setTimeout(() => setToast(null), 3500);
  }, []);

  // Refs
  const iframeRef      = useRef<HTMLIFrameElement | null>(null);
  const audioCtxRef    = useRef<AudioContext | null>(null);
  const stopRingRef    = useRef<(() => void) | null>(null);
  const isOpenRef      = useRef(false);
  const incomingRingRef = useRef(false);
  // Track last processed signal timestamp to avoid re-processing same signal
  const lastSignalTsRef = useRef<number>(0);

  // Unique room name for planning audio
  const roomName = `sk-plan-audio-${connectionId.slice(0, 16)}`;

  const jitsiConfig = [
    'config.prejoinConfig.enabled=false',
    'config.prejoinPageEnabled=false',
    'config.requireDisplayName=false',
    'config.startWithAudioMuted=false',
    'config.startWithVideoMuted=true',
    'config.disableDeepLinking=true',
    'config.enableWelcomePage=false',
    'config.enableClosePage=false',
    'config.enableInsecureRoomNameWarning=false',
    'config.enableLobbyChat=false',
    'config.hideLobbyButton=true',
    'config.p2p.enabled=true',
    'config.filmstrip.disableStageFilmstrip=true',
    'config.disableFilmstripAutohiding=true',
    'config.startAudioOnly=true',
    `config.subject=${encodeURIComponent(JSON.stringify('Planning Audio'))}`,
    'interfaceConfig.SHOW_JITSI_WATERMARK=false',
    'interfaceConfig.SHOW_WATERMARK_FOR_GUESTS=false',
    'interfaceConfig.SHOW_BRAND_WATERMARK=false',
    'interfaceConfig.SHOW_POWERED_BY=false',
    'interfaceConfig.SHOW_PROMOTIONAL_CLOSE_PAGE=false',
    'interfaceConfig.SHOW_CHROME_EXTENSION_BANNER=false',
    'interfaceConfig.HIDE_DEEP_LINKING_LOGO=true',
    'interfaceConfig.HIDE_INVITE_MORE_HEADER=true',
    'interfaceConfig.MOBILE_APP_PROMO=false',
    `interfaceConfig.TOOLBAR_BUTTONS=${encodeURIComponent('["microphone","hangup"]')}`,
    `interfaceConfig.DEFAULT_REMOTE_DISPLAY_NAME=${encodeURIComponent(JSON.stringify(otherUserName))}`,
    `interfaceConfig.DEFAULT_LOCAL_DISPLAY_NAME=${encodeURIComponent(JSON.stringify(currentUserName || 'Jij'))}`,
  ].join('&');

  const userInfoParam = currentUserName
    ? `&userInfo.displayName=${encodeURIComponent(JSON.stringify(currentUserName))}`
    : '';

  const meetUrl = `https://video.superkonnected.com/${roomName}#${jitsiConfig}${userInfoParam}`;

  // Keep refs in sync with state for use inside snapshot callbacks
  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);
  useEffect(() => { incomingRingRef.current = incomingRing; }, [incomingRing]);

  // ── Ring sound helpers ─────────────────────────────────────────────────────
  const startRingSound = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      if (audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume();
      stopRingRef.current = createBLERing(audioCtxRef.current);
    } catch {}
  }, []);

  const stopRingSound = useCallback(() => {
    stopRingRef.current?.();
    stopRingRef.current = null;
  }, []);

  // ── Firestore: write call signal ───────────────────────────────────────────
  const writeSignal = useCallback(async (action: CallSignalAction) => {
    try {
      await updateDoc(doc(db, 'connections', connectionId), {
        planningCallSignal: {
          action,
          fromUserId: currentUserId,
          fromUserName: currentUserName,
          ts: Date.now(),
        },
      });
    } catch (err) {
      console.warn('[AudioChannel] writeSignal error:', err);
    }
  }, [connectionId, currentUserId, currentUserName]);

  // ── Firestore: listen for incoming signals ─────────────────────────────────
  useEffect(() => {
    if (!connectionId) return;
    const connRef = doc(db, 'connections', connectionId);
    const unsub = onSnapshot(connRef, (snap) => {
      if (!snap.exists()) return;
      const signal = snap.data()?.planningCallSignal;
      if (!signal) return;

      // Ignore own signals and already-processed signals
      if (signal.fromUserId === currentUserId) return;
      if (signal.ts && signal.ts <= lastSignalTsRef.current) return;
      lastSignalTsRef.current = signal.ts || 0;

      if (signal.action === 'ring') {
        setIncomingFrom(signal.fromUserName || otherUserName);
        setIncomingCallerId(signal.fromUserId || '');
        setIncomingRing(true);
        startRingSound();
      } else if (signal.action === 'hangup' || signal.action === 'idle') {
        const wasOpen = isOpenRef.current || incomingRingRef.current;
        setIncomingRing(false);
        stopRingSound();
        setIsOpen(false);
        setIsConnecting(false);
        if (wasOpen) {
          showMiniToast(`${signal.fromUserName || otherUserName} heeft opgehangen`, 'info');
        }
      } else if (signal.action === 'declined') {
        setIsOpen(false);
        setIsConnecting(false);
        showMiniToast(`${signal.fromUserName || otherUserName} heeft het gesprek geweigerd`, 'error');
      } else if (signal.action === 'accepted') {
        setIsConnecting(false);
        showMiniToast(`${signal.fromUserName || otherUserName} heeft opgenomen`, 'success');
      }
    });
    return () => unsub();
  }, [connectionId, currentUserId, otherUserName, startRingSound, stopRingSound]);

  // ── Caller: close channel / hang up ───────────────────────────────────────
  // Defined BEFORE handleMicClick so the reference is stable when captured.
  const handleClose = useCallback(async () => {
    setIsOpen(false);
    setIsConnecting(false);
    await writeSignal('hangup');
    showMiniToast('Gesprek beëindigd', 'info');
  }, [writeSignal, showMiniToast]);

  // ── Caller: mic button click → show "Wil je spreken?" bevestiging ──────────
  const handleMicClick = useCallback(() => {
    if (isOpen) {
      handleClose();
    } else {
      setShowConfirm(true);
    }
  }, [isOpen, handleClose]);

  // ── Caller: bevestigd "Ja, bel" ────────────────────────────────────────────
  const handleConfirmCall = useCallback(async () => {
    setShowConfirm(false);
    setIsConnecting(true);
    setIsOpen(true);
    await writeSignal('ring');
    setTimeout(() => setIsConnecting(false), 3000);
  }, [writeSignal]);

  // ── Caller: annuleer bevestiging ───────────────────────────────────────────
  const handleCancelConfirm = useCallback(() => {
    setShowConfirm(false);
  }, []);

  // ── Callee: accept incoming ring ──────────────────────────────────────────
  const handleAcceptRing = useCallback(async () => {
    stopRingSound();
    setIncomingRing(false);
    setIsOpen(true);
    setIsConnecting(true);
    // Notify caller that we accepted
    try {
      await updateDoc(doc(db, 'connections', connectionId), {
        planningCallSignal: {
          action: 'accepted',
          fromUserId: currentUserId,
          fromUserName: currentUserName,
          ts: Date.now(),
        },
      });
    } catch {}
    setTimeout(() => setIsConnecting(false), 3000);
  }, [stopRingSound, connectionId, currentUserId, currentUserName]);

  // ── Callee: decline incoming ring ─────────────────────────────────────────
  const handleDeclineRing = useCallback(async () => {
    stopRingSound();
    setIncomingRing(false);
    try {
      await updateDoc(doc(db, 'connections', connectionId), {
        planningCallSignal: {
          action: 'declined',
          fromUserId: currentUserId,
          fromUserName: currentUserName,
          ts: Date.now(),
        },
      });
    } catch {}
    showMiniToast('Gesprek geweigerd', 'info');
  }, [stopRingSound, connectionId, currentUserId, currentUserName, showMiniToast]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRingSound();
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, [stopRingSound]);

  return (
    <>
      {/* ── BEVESTIGINGSKAART: "Wil je [naam] spreken?" ──────────────────── */}
      {showConfirm && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: 'rgba(0,0,0,0.45)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '24px',
              padding: '32px 28px',
              maxWidth: '300px',
              width: '100%',
              boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
              textAlign: 'center',
              animation: 'sk-ring-card-in 0.28s cubic-bezier(0.34,1.56,0.64,1)',
            }}
          >
            {/* Icon */}
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'rgba(234,88,12,0.10)',
                border: '2px solid rgba(234,88,12,0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}
            >
              <Microphone size={28} weight="bold" color="#EA580C" />
            </div>

            <p style={{ fontSize: '11px', fontWeight: 700, color: '#EA580C', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '6px' }}>
              Audio kanaal
            </p>
            <h2 style={{ fontSize: '19px', fontWeight: 700, color: '#1f2937', marginBottom: '6px', lineHeight: 1.25 }}>
              Wil je {otherUserName} spreken?
            </h2>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '28px' }}>
              {otherUserName} ontvangt een melding om op te nemen.
            </p>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleCancelConfirm}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '14px',
                  border: '1.5px solid #E5E7EB',
                  background: '#F9FAFB',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = '#F3F4F6')}
                onMouseLeave={e => (e.currentTarget.style.background = '#F9FAFB')}
              >
                Annuleer
              </button>
              <button
                onClick={handleConfirmCall}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '14px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #EA580C, #F97316)',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(234,88,12,0.35)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  transition: 'opacity 0.15s',
                }}
                onMouseEnter={e => (e.currentTarget.style.opacity = '0.9')}
                onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
              >
                <Phone size={16} weight="bold" />
                Ja, bel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── INKOMEND GESPREK KAART (User 2) ──────────────────────────────── */}
      {incomingRing && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9998,
            background: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(5px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '24px',
              padding: '32px 28px',
              maxWidth: '320px',
              width: '100%',
              boxShadow: '0 24px 60px rgba(0,0,0,0.3)',
              textAlign: 'center',
              animation: 'sk-ring-card-in 0.3s cubic-bezier(0.34,1.56,0.64,1)',
            }}
          >
            {/* Pulsing top bar */}
            <div
              style={{
                height: '4px',
                borderRadius: '2px',
                background: 'linear-gradient(90deg, #EA580C, #F97316)',
                marginBottom: '20px',
                animation: 'sk-bar-pulse 1s ease-in-out infinite',
              }}
            />

            {/* Pulsing mic icon */}
            <div
              style={{
                width: '72px',
                height: '72px',
                borderRadius: '50%',
                background: 'rgba(249,115,22,0.12)',
                border: '2px solid rgba(249,115,22,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                animation: 'sk-icon-pulse 1.4s ease-in-out infinite',
              }}
            >
              <Microphone size={32} weight="bold" color="#EA580C" />
            </div>

            <p style={{ fontSize: '11px', fontWeight: 700, color: '#EA580C', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '6px' }}>
              Inkomend gesprek
            </p>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1f2937', marginBottom: '4px' }}>
              {incomingFrom}
            </h2>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '28px' }}>
              wil je spreken via audio
            </p>

            {/* Accept / Decline */}
            <div style={{ display: 'flex', gap: '20px', justifyContent: 'center' }}>
              {/* Wijs af */}
              <button
                onClick={handleDeclineRing}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                <div style={{
                  width: '60px', height: '60px', borderRadius: '50%',
                  background: '#FEF2F2', border: '2px solid #FECACA',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'transform 0.15s',
                }}
                  onMouseEnter={e => ((e.currentTarget as HTMLDivElement).style.transform = 'scale(1.07)')}
                  onMouseLeave={e => ((e.currentTarget as HTMLDivElement).style.transform = 'scale(1)')}
                >
                  <PhoneX size={24} weight="bold" color="#DC2626" />
                </div>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#DC2626' }}>Wijs af</span>
              </button>

              {/* Neem op */}
              <button
                onClick={handleAcceptRing}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                <div style={{
                  width: '60px', height: '60px', borderRadius: '50%',
                  background: '#EA580C',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 6px 18px rgba(234,88,12,0.4)',
                  transition: 'transform 0.15s',
                }}
                  onMouseEnter={e => ((e.currentTarget as HTMLDivElement).style.transform = 'scale(1.07)')}
                  onMouseLeave={e => ((e.currentTarget as HTMLDivElement).style.transform = 'scale(1)')}
                >
                  <Phone size={24} weight="bold" color="white" />
                </div>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#EA580C' }}>Neem op</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CHANNEL STATUS STRIP ──────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '6px 10px',
          background: isOpen ? '#FFF7ED' : '#F9FAFB',
          border: `1px solid ${isOpen ? '#FED7AA' : '#E5E7EB'}`,
          borderRadius: '10px',
          transition: 'all 0.2s ease',
        }}
      >
        {/* Toggle button */}
        <button
          onClick={handleMicClick}
          disabled={isConnecting}
          title={isOpen ? 'Audio kanaal sluiten' : 'Audio kanaal openen'}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            border: 'none',
            cursor: isConnecting ? 'wait' : 'pointer',
            background: isOpen ? '#DC2626' : '#EA580C',
            color: '#fff',
            transition: 'background 0.2s ease',
            flexShrink: 0,
          }}
        >
          {isOpen
            ? <PhoneX size={15} weight="bold" />
            : <Microphone size={15} weight="bold" />
          }
        </button>

        {/* Status */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
          <div
            style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: isOpen
                ? (isConnecting ? '#F59E0B' : '#22C55E')
                : '#9CA3AF',
              flexShrink: 0,
              animation: isOpen && !isConnecting ? 'sk-dot-pulse 2s infinite' : 'none',
            }}
          />
          <span
            style={{
              fontSize: '11px',
              color: isOpen ? '#9A3412' : '#6B7280',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {isOpen
              ? (isConnecting ? `Verbinden met ${otherUserName}…` : `Kanaal open · ${otherUserName}`)
              : 'Audio kanaal'
            }
          </span>

          {/* Live audio bar */}
          {isOpen && !isConnecting && (
            <div style={{
              flex: 1,
              height: '4px',
              background: '#FED7AA',
              borderRadius: '2px',
              overflow: 'hidden',
              maxWidth: '80px',
            }}>
              <div style={{
                width: '60%',
                height: '100%',
                background: 'linear-gradient(90deg, #EA580C, #F97316)',
                borderRadius: '2px',
                animation: 'sk-audio-bar 1.5s ease-in-out infinite alternate',
              }} />
            </div>
          )}
        </div>
      </div>

      {/* Hidden Jitsi iframe */}
      {isOpen && (
        <iframe
          ref={iframeRef}
          src={meetUrl}
          allow="microphone; autoplay"
          style={{
            width: '1px', height: '1px',
            position: 'absolute', left: '-9999px',
            opacity: 0, pointerEvents: 'none',
          }}
          title="Planning Audio Channel"
        />
      )}

      {/* ── MINI TOAST ───────────────────────────────────────────────────── */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: '28px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10100,
            padding: '10px 20px',
            borderRadius: '999px',
            fontSize: '13px',
            fontWeight: 600,
            color: '#fff',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
            animation: 'sk-toast-in 0.25s cubic-bezier(0.34,1.56,0.64,1)',
            background:
              toast.type === 'success' ? '#16A34A'
              : toast.type === 'error' ? '#DC2626'
              : '#374151',
          }}
        >
          {toast.type === 'success' ? '✓ ' : toast.type === 'error' ? '✕ ' : 'ℹ '}
          {toast.msg}
        </div>
      )}

      <style>{`
        @keyframes sk-toast-in {
          from { opacity: 0; transform: translateX(-50%) translateY(12px) scale(0.92); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0)     scale(1);    }
        }
        @keyframes sk-dot-pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.3; }
        }
        @keyframes sk-audio-bar {
          0%   { width: 15%; }
          100% { width: 85%; }
        }
        @keyframes sk-ring-card-in {
          from { transform: scale(0.85) translateY(20px); opacity: 0; }
          to   { transform: scale(1) translateY(0);      opacity: 1; }
        }
        @keyframes sk-bar-pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.35; }
        }
        @keyframes sk-icon-pulse {
          0%, 100% { transform: scale(1);    opacity: 0.9; }
          50%       { transform: scale(1.12); opacity: 1;   }
        }
      `}</style>
    </>
  );
};

export default PlanningAudioChannel;
